import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'

import { api } from '@/app/services/api'
import { useLazyGetContactQuery, useLazyGetCustomerQuery } from '@/app/services/customer'
import { useLazyGetStoreSettingsQuery } from '@/app/services/organisation'
import { useGetOrCreateUserContextMutation } from '@/app/services/user'
import {
    selectAppIsLoading,
    selectCurrentUser,
    setAppIsLoading,
    setLanguage,
    setLocale,
    setUserRole,
} from '@/app/slices/appSlice'
import {
    selectOrganisation,
    selectOrganisationId,
    selectPlanMetadata,
    setOrganisation,
    setOrganisationId,
} from '@/app/slices/organisationSlice'
import { PageSpinner } from '@/common/components'
import { LANGUAGE_STORAGE_KEY } from '@/common/components/LanguageSelectorButton/LanguageSelectorButton'
import {
    determinePaidFeatures,
    extractSubscriptionMetadata,
    resolveMetadata,
} from '@/common/helpers/organisationHelpers'
import { useCustomDomain } from '@/common/hooks/useCustomDomain'
import useEffectOnlyOnUpdate from '@/common/hooks/useEffectOnlyOnUpdate'
import { getLanguageToUse, getLocaleToUse, initUserFlow, ORDER_USER_TYPE, Paths, setPageBranding } from '@/common/utils'
import Layout from '@/layout/Layout'

const UserRequiredContainer = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const appInsights = useAppInsightsContext()
    const { isLoadingOrgId, isSuccessOrgId } = useCustomDomain()

    const { i18n } = useTranslation()

    const { getToken, isAuthenticated, isLoading, user } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const appIsLoading = useSelector(selectAppIsLoading)
    const organisation = useSelector(selectOrganisation)
    const currentUser = useSelector(selectCurrentUser)
    const planMetadata = useSelector(selectPlanMetadata)

    const [getStoreSettings] = useLazyGetStoreSettingsQuery()
    const [getOrCreateUserContext] = useGetOrCreateUserContextMutation()
    const [getCustomer] = useLazyGetCustomerQuery()
    const [getContact] = useLazyGetContactQuery()

    const trackEvent = ({ data, eventName }) => appInsights.trackEvent({ name: eventName, properties: data })

    const determineRedirectPath = (currentPath) => {
        if (currentPath === '/') {
            return Paths.HOME_PATHNAME.replace(':organisationId', organisationId)
        }
        return null
    }

    // Initialize organization data
    const initOrganization = async () => {
        try {
            const organisationResult = await getStoreSettings({ organisationId }).unwrap()

            // Check if organization has web store feature enabled
            const metadata = extractSubscriptionMetadata(organisationResult?.subscriptions)
            const resolvedMetadata = resolveMetadata(metadata)
            const paidFeatures = determinePaidFeatures(
                resolvedMetadata,
                (organisationResult?.trial && !organisationResult?.trial.hasExpired) ?? false,
                organisationResult?.hasActiveSubscription
            )

            // If web store is not available, redirect to https://tempustools.com
            if (!paidFeatures.hasWebStore) {
                console.error('Organisation does not have web store feature enabled')
                trackEvent({
                    eventName: 'Web store feature not available',
                    data: { organisationId, organisation: organisationResult },
                })
                window.location.href = 'https://tempustools.com'
                return null
            }

            // Set comprehensive page branding including SEO metadata
            // Priority: webStoreDisplayName > organisationName > null (function will use "Web Store" fallback)
            setPageBranding({
                description: organisationResult?.webStoreDescription || null,
                favicon: organisationResult?.webStoreFaviconUri || null,
                logo: organisationResult?.webStoreLogoUri || null,
                themeColor: organisationResult?.webStorePrimaryColour || null,
                title: organisationResult?.webStoreDisplayName || organisationResult?.organisationName || null,
                url: window.location.href,
            })
            return organisationResult
        } catch (error) {
            if ([400, 401, 404].includes(error.status) && location.pathname !== Paths.NOT_FOUND) {
                console.error('Organisation not found, redirecting to not found page')
                trackEvent({
                    eventName: `Organisation not found, redirected to ${Paths.NOT_FOUND}`,
                    data: { currentUser: null, organisation: null },
                })
                navigate(Paths.NOT_FOUND)
            } else if (location.pathname !== Paths.ERROR_PATHNAME) {
                console.error('Failed to load organisation settings:', error)
                trackEvent({
                    eventName: `Organisation load failed, redirected to ${Paths.ERROR_PATHNAME}`,
                    data: { error: error?.status || 'unknown', organisationId },
                })
                navigate(Paths.ERROR_PATHNAME)
            }
            dispatch(api.util.resetApiState())
            dispatch(setAppIsLoading(false))
            return null
        }
    }

    // Initialize user data if authenticated
    const initUser = async () => {
        if (!isAuthenticated || !user || (currentUser && Object.keys(currentUser).length > 0)) {
            return null
        }

        await getToken()

        let userContext
        try {
            userContext = await getOrCreateUserContext({
                organisationId,
                user: { firstName: user?.givenName, lastName: user?.familyName, emailAddress: user?.email },
            }).unwrap()

            // Fetch customer and contact info in parallel
            await Promise.all([
                getCustomer({ customerId: userContext?.customerId, organisationId }),
                getContact({
                    customerId: userContext?.customerId,
                    contactId: userContext?.contactId,
                    organisationId,
                }),
            ])
        } catch (error) {
            userContext = error
        }

        if (!userContext?.user) {
            trackEvent({
                eventName: 'User not found, redirected to error page',
                data: { user, userContext },
            })
            navigate(Paths.NOT_FOUND)
            dispatch(setAppIsLoading(false))
            return null
        }

        return userContext.user
    }

    // Set up application context with organization and user data
    const setupApplicationContext = (organisationResult, appUser) => {
        const role = appUser ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
        const localeToUse = getLocaleToUse(organisationResult)
        const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY)
        const languageToUse =
            storedLanguage || getLanguageToUse(appUser || currentUser, organisationResult) || navigator.language
        const userId = appUser?.userId || `guest_${organisationId}`

        // Update application state (organisation already set in initOrganization)
        i18n.changeLanguage(languageToUse)
        dispatch(setOrganisation(organisationResult))
        dispatch(setOrganisationId(organisationId))
        dispatch(setUserRole(role))
        dispatch(setLanguage(languageToUse))
        dispatch(setLocale(localeToUse))
        dispatch(setAppIsLoading(false))

        // Initialize user flow
        initUserFlow({
            userId,
            organisation: organisationResult,
            organisationId,
            language: languageToUse,
            planMetadata,
        })
    }

    // Handle any necessary redirections
    const handleRedirection = () => {
        const redirectTo = determineRedirectPath(location.pathname)
        if (redirectTo) {
            trackEvent({
                eventName: `User redirected to ${redirectTo}`,
                data: { currentUser, organisation },
            })
            navigate(redirectTo)
        } else {
            trackEvent({
                eventName: 'User not redirected',
                data: { currentUser, organisation },
            })
        }
    }

    const initUserAndOrg = async () => {
        // Initialize organization first
        const organisationResult = await initOrganization()
        if (!organisationResult) return

        // Initialize user if authenticated
        const appUser = await initUser()

        // Set up application with organization and user data
        setupApplicationContext(organisationResult, appUser)

        // Handle any redirects
        handleRedirection()
    }

    // initUserAndOrg intentionally omitted from deps - we want to run only when auth/org state changes
    useEffect(() => {
        if (!isLoading && !isLoadingOrgId && isSuccessOrgId && organisationId) {
            initUserAndOrg()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isSuccessOrgId, isLoadingOrgId, organisationId])

    useEffectOnlyOnUpdate(() => {
        if (organisation && currentUser) {
            const languageToUse = getLanguageToUse(currentUser, organisation)
            initUserFlow({
                userId: currentUser?.userId || `guest_${organisationId}`,
                organisation,
                organisationId,
                language: languageToUse,
                planMetadata,
            })
        }
    }, [organisation, currentUser, isAuthenticated, location.pathname])

    return appIsLoading ? (
        <PageSpinner />
    ) : (
        <Layout>
            <Outlet />
        </Layout>
    )
}

UserRequiredContainer.propTypes = {}

export default UserRequiredContainer
